/**
 * Renders the marketing-route page matched against the current URL.
 *
 * Used both server-side (inside StaticRouter) and client-side (inside
 * BrowserRouter) — must produce identical JSX given the same inputs.
 *
 * Always renders via <Switch> so client-side navigation between marketing
 * routes works. SSR `data` is only forwarded to the route that matched the
 * initial URL — for any other route the user navigates to client-side, the
 * page falls back to its own data-fetch path (e.g. Blog and BlogArticleRoute re-fetch
 * through GraphQL).
 *
 * Each route's page is resolved via `getResolvedMarketingPage` — synchronous by construction on
 * the server (`entry-server.tsx` registers every page at module load) and on the client's first
 * render whenever the SSR-matched page has already resolved. `lazyMarketingPage`'s `<Suspense>` is
 * only reached for a route the visitor navigates to client-side that has not resolved yet — see
 * the no-top-level-Suspense comment below for why that boundary can't sit any higher.
 */

import { initAppsflyerOneLink } from "components/common/useAppsflyerOneLink";
import trackEvent from "components/tracking/trackEvent";
import { MARKETING_LANG_CODES, stripLangPrefix } from "i18n/urlLocale";
import { MarketingLangContext } from "i18n/useMarketingLang";
import React, { Suspense, useEffect, useRef } from "react";
import { Route, Switch, useLocation } from "react-router-dom";
import { getMarketingTranslations } from "./i18n";
import { getResolvedMarketingPage, lazyMarketingPage } from "./marketingPages";
import { HostFlavor, MARKETING_ROUTES, matchRoutePath } from "./marketingRoutes";

const NON_EN_LANGS = MARKETING_LANG_CODES.filter((lang) => lang !== "en");

export interface MarketingShellProps {
	hostFlavor: HostFlavor;
	/** Output of the route's data loader (server-side fetch) — passed to the matching page as `initialData`. */
	data?: any;
	/** Path that was matched server-side. The route at this path receives the SSR data; others re-fetch. */
	initialPath?: string;
}

// No top-level Suspense here — each route wraps only its own `Lazy` page (below) in a
// <Suspense> boundary, and only when that page hasn't resolved yet. A top-level Suspense
// converts framer-motion SSR style mismatches into fatal #422 errors and a blank page instead
// of the recoverable #418 warnings React normally emits and self-corrects, and SSR/the first
// client render never hit Suspense at all — see this file's top comment.
export const MarketingShell: React.FC<MarketingShellProps> = ({ hostFlavor, data, initialPath }) => {
	const routes = MARKETING_ROUTES.filter((r) => r.hostFlavors.includes(hostFlavor));
	const location = useLocation();
	// Track per-pathname so the same path never fires twice (guards against
	// strict-mode double-invoke and mount+hydrate racing).
	const firedPathRef = useRef<string | null>(null);

	useEffect(() => {
		// SSR safety: never run during server-side rendering.
		if (typeof window === "undefined") return;
		const path = location.pathname;
		if (firedPathRef.current === path) return;
		firedPathRef.current = path;
		trackEvent(
			"landing_page_viewed",
			{
				path,
				referrer: document.referrer || undefined,
			},
			"marketing_site"
		);
	}, [location.pathname]);

	useEffect(() => {
		// SSR safety: never run during server-side rendering.
		if (typeof window === "undefined") return;
		initAppsflyerOneLink();
	}, []);

	// Derived from the URL (not a prop) so server (StaticRouter) and client (BrowserRouter)
	// always agree on the same value — a prop threaded separately from each render call risks
	// server/client drift, which for actual rendered copy (not just meta) is a hydration mismatch.
	const { lang } = stripLangPrefix(location.pathname);
	const uiTranslations = getMarketingTranslations(lang);

	return (
		// Every component under this tree — not just the routed page, every section, card and nav
		// piece nested arbitrarily deep below it — can call `useMarketingLang()` and get this same
		// `lang` without it being threaded through as a prop at each level. This is what fixes the
		// SSR/hydration mismatch: those components used to derive their own answer from `window`,
		// which isn't there yet on the server and always resolved to "en" there regardless of the
		// real URL, then flipped to the right locale after hydration. See `useMarketingLang.ts`'s
		// doc comment for the full resolution order this is tier 1 of.
		<MarketingLangContext.Provider value={lang}>
			<Switch>
				{routes.map((route) => (
					<Route
						key={route.path}
						// Routes that already declare their own locale — hasLoader (blog, /discover
						// guides) or langOverride (the per-locale homepage entries) — have an explicit
						// locale path already and must not be expanded again. Expanding one of those
						// generically would produce nonsense paths like `/de/nl`. Every other marketing
						// route matches its lang-prefixed variants generically.
						path={route.hasLoader || route.langOverride ? route.path : [route.path, ...NON_EN_LANGS.map((l) => `/${l}${route.path}`)]}
						exact
						render={(routeProps) => {
							const isInitialMatch = !!initialPath && matchRoutePath(route.path, initialPath) !== null;
							const props = {
								...routeProps,
								...(route.componentProps || {}),
								initialData: isInitialMatch ? data : undefined,
								lang,
								uiTranslations,
							};
							// Resolved (SSR-registered, or already loaded client-side) → render synchronously,
							// exactly as before this route was split into its own chunk. Only a page that is
							// NOT yet loaded — a client-side navigation to a marketing route after hydration —
							// falls to the lazy path, and only that page's own <Suspense> boundary covers it.
							const Resolved = getResolvedMarketingPage(route.page);
							if (Resolved) return <Resolved {...props} />;
							const Lazy = lazyMarketingPage(route.page);
							return (
								<Suspense fallback={null}>
									<Lazy {...props} />
								</Suspense>
							);
						}}
					/>
				))}
			</Switch>
		</MarketingLangContext.Provider>
	);
};

export default MarketingShell;
